import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretSortIcon,
  ExclamationTriangleIcon,
  LayoutIcon,
} from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Popover,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  Table as ReactTable,
  OnChangeFn,
  getSortedRowModel,
  ColumnSizingState,
  Header,
  Column,
} from "@tanstack/react-table";
import { forwardRef, useRef, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

type DataTableProps = {
  title: string;
  data: any[];
  columns: any[];
  totalCount?: number;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: OnChangeFn<Record<string, boolean>>;
  loading?: boolean;
  isSuccess?: boolean;
  onBottomReach?: () => void;
  sorting?: any[];
  onSortingChange?: (value: any) => void;
  renderActions?: React.ReactNode;
  renderHeader?: React.ReactNode;
  renderPagination?: React.ReactNode;
  rowClassName?: (row: any) => string;
  canToggleView?: boolean;
  visibleColumnNames?: Record<string, string>;
  emptyTitle: string;
  emptyDescription: string;
};

type View = "table" | "grid";

export function DataTable({
  title,
  data,
  columns,
  totalCount = 0,
  loading,
  isSuccess,
  rowClassName,
  rowSelection,
  onRowSelectionChange,
  sorting,
  onSortingChange,
  renderActions,
  renderHeader,
  renderPagination,
  canToggleView,
  visibleColumnNames,
  emptyTitle,
  emptyDescription,
}: DataTableProps) {
  const [columnVisibility, setColumnVisibility] = useState({});
  const [view, setView] = useState<View>("table");
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    JSON.parse(localStorage.getItem(title) || "{}")
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      rowSelection,
      sorting,
      columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange,
    enableMultiSort: true,
    manualSorting: true,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    defaultColumn: {
      size: 200, //starting column size
    },
    // Use row.original.id as the row selection key
    getRowId: (row) => row.id,
  });

  const { rows } = table.getRowModel();
  const [scrollParent, setScrollParent] = useState<HTMLDivElement>();

  const updateColumnSize = (headerId: string, newWidth: number) => {
    setColumnSizing((prevState) => {
      const newState = {
        ...prevState,
        [headerId]: newWidth,
      };
      localStorage.setItem(title, JSON.stringify(newState));
      return newState;
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg flex-grow pb-1">
      {renderHeader ? (
        renderHeader
      ) : (
        <Flex justify="between" align="center" p="2">
          <Flex align="center" gap="2">
            <Text size="4" weight="bold">
              {title}
            </Text>
            <Text size="2" color="gray" weight="medium">
              ({totalCount} orders)
            </Text>
          </Flex>
          <Flex align="center">
            {renderPagination}
            {!!visibleColumnNames && (
              <VisibleColumns
                table={table}
                visibleColumnNames={visibleColumnNames}
              />
            )}
            {canToggleView && (
              <SegmentedControl.Root
                ml="2"
                value={view}
                onValueChange={(value) => setView(value as View)}
              >
                <SegmentedControl.Item value="grid">
                  Grid View
                </SegmentedControl.Item>
                <SegmentedControl.Item value="table">
                  Table View
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            )}
          </Flex>
        </Flex>
      )}
      {renderActions}
      <Flex className="h-[calc(100dvh-176px)]">
        <ScrollArea
          className="relative w-full border-t border-[var(--gray-7)] max-h-[calc(100dvh-176px)] disable-overscroll"
          ref={(el) => setScrollParent(el ?? undefined)}
        >
          <TableVirtuoso
            useWindowScroll
            customScrollParent={scrollParent}
            totalCount={loading ? 20 : rows.length}
            components={{
              Table: ({ style, ...props }) => (
                <table
                  {...props}
                  className="!table-fixed border-separate border-spacing-0 text-sm after:absolute after:content-[''] after:w-full after:h-[44px] after:bg-gray-100 after:top-0 after:shadow-[inset_0_-1px_var(--gray-7)] w-full"
                  style={{
                    ...style,
                    width: `max(100%, ${table.getTotalSize()}px)`,
                  }}
                />
              ),
              TableHead: forwardRef((props, ref) => {
                return (
                  <thead
                    {...props}
                    ref={ref}
                    className="bg-gray-100 z-10 sticky top-0"
                  />
                );
              }),
              TableRow: (props) => {
                const rowIndex = props["data-index"];
                const row = rows[rowIndex];

                if (loading) {
                  return (
                    <tr {...props} className="h-12">
                      {new Array(columns.length).fill(null).map((_, index) => (
                        <td
                          key={index}
                          className={twMerge([
                            "p-3 shadow-[inset_0_-1px_var(--gray-7)] border-l border-[var(--gray-7)]",
                            index === 0 && "border-l-0",
                            rowIndex === rows.length - 1 && "shadow-none",
                          ])}
                        >
                          <Skeleton />
                        </td>
                      ))}
                    </tr>
                  );
                }

                return (
                  <tr {...props}>
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={twMerge([
                          "bg-white relative p-3 shadow-[inset_0_-1px_var(--gray-7)] border-l border-[var(--gray-7)] group",
                          index === 0 && "border-l-0",
                          rowIndex === rows.length - 1 && "shadow-none",
                          !loading && rowClassName?.(row.original),
                        ])}
                        {...{
                          style: {
                            width:
                              columnSizing[cell.column.id] ||
                              cell.column.getSize(),
                          },
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                );
              },
            }}
            fixedHeaderContent={() => {
              return table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <Th
                      key={header.id}
                      header={header}
                      index={index}
                      columnSizing={columnSizing}
                      updateColumnSize={updateColumnSize}
                      getTotalSize={table.getTotalSize}
                    />
                  ))}
                </tr>
              ));
            }}
          />

          {data.length === 0 && isSuccess && !loading && (
            <Flex
              justify="center"
              align="center"
              direction="column"
              className="absolute h-[calc(100%-100px)] w-full top-12 left-0 bg-white z-10"
            >
              <ExclamationTriangleIcon
                width={64}
                height={64}
                color="var(--orange-11)"
                className="mb-2"
              />
              <Text as="p" size="5" weight="bold" align="center" mb="1">
                {emptyTitle || "No data available"}
              </Text>
              <Text as="p" size="3">
                {emptyDescription}
              </Text>
            </Flex>
          )}
        </ScrollArea>
      </Flex>
    </div>
  );
}

export function Th({
  header,
  index,
  columnSizing,
  updateColumnSize,
  getTotalSize,
}: {
  header: Header<any, unknown>;
  index: number;
  columnSizing: Record<string, number>;
  getTotalSize: () => number;
  updateColumnSize: (headerId: string, newWidth: number) => void;
}) {
  const columnCoords = useRef({ x: 0, width: 0 });
  const ref = useRef<HTMLTableCellElement | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    columnCoords.current.x = e.clientX;

    const styles = window.getComputedStyle(ref.current!);
    columnCoords.current.width = parseInt(styles.width, 10);
    e.currentTarget.classList.add("isResizing");
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      const dx = Math.floor(e.clientX - columnCoords.current.x);
      const table = document.querySelector("table") as HTMLTableElement;

      const width = columnCoords.current.width;
      ref.current!.style.width = width + dx + "px";
      const totalSize = getTotalSize() + dx;
      table.style.width = `max(100%, ${totalSize}px)`;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      columnCoords.current.width = 0;
      columnCoords.current.x = 0;

      e.currentTarget.classList.remove("isResizing");
      updateColumnSize(header.id, parseInt(ref.current!.style.width));
    }
  };

  const handleSortClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const currentSort = header.column.getIsSorted();
    const newSort = currentSort === "asc" ? "desc" : "asc";
    header.column.toggleSorting(newSort === "desc");
  };

  return (
    <th
      ref={ref}
      className={twMerge([
        "relative p-3 text-left border-l border-[var(--gray-7)] shadow-[inset_0_-1px_var(--gray-7)]",
        index === 0 && "border-l-0",
      ])}
      {...{
        colSpan: header.colSpan,
        style: {
          minWidth: columnSizing[header.id] || header.getSize(),
          width: columnSizing[header.id] || header.getSize(),
        },
      }}
    >
      <div className="flex items-center justify-between min-h-[32px]">
        <span className="text-xs leading-tight whitespace-pre-line pr-6 py-1">
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </span>

        {header.column.getCanSort() && (
          <Button
            size="1"
            variant="ghost"
            className="p-1 absolute right-3 top-1/2 -translate-y-1/2"
            color="gray"
            title={
              header.column.getCanSort()
                ? header.column.getIsSorted() === "asc"
                  ? "Sort descending"
                  : "Sort ascending"
                : undefined
            }
            onClick={handleSortClick}
          >
            {header.column.getIsSorted() ? (
              <>
                {header.column.getIsSorted() === "asc" ? (
                  <ArrowUpIcon />
                ) : (
                  <ArrowDownIcon />
                )}
              </>
            ) : (
              <CaretSortIcon />
            )}
          </Button>
        )}
      </div>
      {!["select"].includes(header.id) && (
        <div
          className="resizer w-1"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
      )}
    </th>
  );
}

function ColumnGroup({
  columns,
  title,
  visibleColumnNames,
}: {
  columns: Column<unknown, unknown>[];
  title: string;
  visibleColumnNames?: Record<string, string>;
}) {
  if (columns.length === 0) return null;

  return (
    <>
      <Text size="2" weight="bold" color="gray" mt="3" mb="1" as="p">
        {title}
      </Text>
      {columns.map((column) => (
        <Box py="1" key={column.id}>
          <Text as="label" className="select-none" size="2">
            <Flex gap="2">
              <Checkbox
                checked={column.getIsVisible()}
                onCheckedChange={(checked) => {
                  column.getToggleVisibilityHandler()({
                    target: {
                      checked,
                    },
                  });
                }}
              />
              {visibleColumnNames?.[column.id]}
            </Flex>
          </Text>
        </Box>
      ))}
    </>
  );
}

export function VisibleColumns({
  table,
  visibleColumnNames,
}: {
  table: ReactTable<any>;
  visibleColumnNames?: Record<string, string>;
}) {
  const allColumns = table
    .getAllLeafColumns()
    .filter((column) => column.id !== "select");

  const staticColumns = allColumns.filter(
    (column) =>
      !column.id.startsWith("stage_") &&
      !column.id.startsWith("rejection_") &&
      column.id !== "anomaly_report"
  );

  const stageColumns = allColumns.filter((column) =>
    column.id.startsWith("stage_")
  );

  const rejectionColumns = allColumns.filter((column) =>
    column.id.startsWith("rejection_")
  );

  const anomalyColumn = allColumns.filter(
    (column) => column.id === "anomaly_report"
  );

  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button variant="outline" color="gray">
          <LayoutIcon />
        </Button>
      </Popover.Trigger>
      <Popover.Content style={{ width: 250 }}>
        <Text size="3" weight="bold" mb="2" as="p">
          Columns
        </Text>

        {/* Static/Core Columns */}
        <ColumnGroup
          columns={staticColumns}
          title="Order Columns"
          visibleColumnNames={visibleColumnNames}
        />

        {/* Stage Columns */}
        <ColumnGroup
          columns={stageColumns}
          title="Stage Status"
          visibleColumnNames={visibleColumnNames}
        />

        {/* Rejection Columns */}
        <ColumnGroup
          columns={rejectionColumns}
          title="Rejection Reasons"
          visibleColumnNames={visibleColumnNames}
        />

        {/* Anomaly Report */}
        <ColumnGroup
          columns={anomalyColumn}
          title="Reports"
          visibleColumnNames={visibleColumnNames}
        />
      </Popover.Content>
    </Popover.Root>
  );
}
